// src/pages/clientes/components/FilialListTable.tsx
import { useMemo, useState } from "react";
import { Store, Building2, Edit, PowerOff, Power, AlertTriangle } from "lucide-react";

import { ClienteFilialDTO } from "@/types/cliente-filial.type";
import { Column, DataTable } from "@/components/table/DataTable";
import { useServerDataTable } from "@/hooks/data-table/use-server-data-table";
import { BooleanBadge } from "@/components/table/BooleanBadge";
import { ActionMenu, ActionMenuItem } from "@/components/shared/ActionMenu";
import { PermissaoEnum } from "@workflow/types";

import { filiaisQueries, filiaisKeys } from "@/services/api/queries/filiais.queries";
import { useFilialActions } from "@/hooks/clientes/use-filial-actions";

import { FilialFormDialog } from "./FilialFormDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { format } from "date-fns";
import { PersonBadge } from "@/components/shared/PersonBadge";

interface FilialListTableProps {
  storageKey: string;
}

export function FilialListTable({ storageKey }: FilialListTableProps) {
  // Camada de dados padronizada. `useFilialActions` já invalida `filiais-list`,
  // então as mutações refrescam a lista sozinhas (sem setState local).
  const { tableProps, isError, invalidate } = useServerDataTable<
    ClienteFilialDTO,
    unknown,
    Record<string, unknown>
  >({
    queryOptions: filiaisQueries.list,
    filters: filiaisQueries.filtros,
    invalidateKey: filiaisKeys.list,
  });

  // Estados dos modais
  const [filialToEdit, setFilialToEdit] = useState<ClienteFilialDTO | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const [filialToToggleStatus, setFilialToToggleStatus] = useState<ClienteFilialDTO | null>(null);
  const [isStatusAlertOpen, setIsStatusAlertOpen] = useState(false);

  const { inactivateFilial, reactivateFilial, isTogglingStatus } = useFilialActions();

  const handleConfirmStatusToggle = async () => {
    if (!filialToToggleStatus) return;

    try {
      if (filialToToggleStatus.ativo) {
        await inactivateFilial(filialToToggleStatus.id);
      } else {
        await reactivateFilial(filialToToggleStatus.id);
      }
    } finally {
      setIsStatusAlertOpen(false);
      setFilialToToggleStatus(null);
    }
  };

  const columns = useMemo<Column<ClienteFilialDTO>[]>(
    () => [
      {
        type: "headless",
        key: "actions",
        title: "Ações",
        visible: true,
        width: "min-w-[40px]",
        render: (filial, onRowClick) => {
          const actions: ActionMenuItem[] = [
            {
              label: "Editar Filial",
              icon: <Edit className="h-4 w-4" />,
              onClick: () => {
                setFilialToEdit(filial);
                setIsFormOpen(true);
              },
              permissions: [PermissaoEnum.ADMIN],
            },
            {
              label: "Separador",
              separator: true,
            },
            {
              label: filial.ativo ? "Inativar" : "Reativar",
              icon: filial.ativo ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />,
              danger: filial.ativo,
              onClick: () => {
                setFilialToToggleStatus(filial);
                setIsStatusAlertOpen(true);
              },
              permissions: [PermissaoEnum.ADMIN],
            },
          ];
          return <ActionMenu items={actions} />;
        },
      },
      {
        key: "filial.id",
        title: "ID",
        sortable: true,
        width: "w-[100px]",
        render: (filial) => <span className="font-medium text-primary">{filial.id}</span>,
        raw: (filial) => filial.id.toString(),
      },
      {
        key: "filial.idPubli",
        title: "ID Publi",
        sortable: true,
        width: "w-[100px]",
        render: (filial) => (
          <span className="font-medium text-primary dark:text-emerald-500">{filial.idPubli}</span>
        ),
        raw: (filial) => filial.idPubli.toString(),
      },
      {
        key: "filial.nome",
        title: "Nome da Filial",
        sortable: true,
        width: "min-w-[250px]",
        render: (filial) => (
          <div className="flex items-center gap-2 py-1">
            <Store className="max-h-4 min-h-4 min-w-4 max-w-4 text-muted-foreground opacity-70" />
            <span className="truncate font-semibold text-foreground" title={filial.nome}>
              {filial.nome}
            </span>
          </div>
        ),
        raw: (filial) => filial.nome,
      },
      {
        key: "filial.grupo",
        title: "Grupo Informado",
        sortable: true,
        render: (filial) => (
          <span className="rounded-md bg-muted px-2 py-0.5 text-sm font-medium text-muted-foreground">
            {filial.grupo || "Nenhum"}
          </span>
        ),
        raw: (f) => f.grupo || "",
      },
      {
        key: "cliente.nome",
        title: "Cliente Vinculado",
        sortable: true,
        width: "min-w-[200px]",
        render: (filial) => {
          if (filial.cliente) {
            return (
              <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                <Building2 className="max-h-4 min-h-4 min-w-4 max-w-4 text-purple-600/70" />
                <span className="truncate" title={filial.cliente.nome}>
                  {filial.cliente.nome}
                </span>
              </div>
            );
          }
          return (
            <Tooltip>
              <TooltipTrigger className="flex cursor-help items-center gap-1.5 text-sm font-medium text-destructive">
                <AlertTriangle className="h-4 w-4" />
                Pendente
              </TooltipTrigger>
              <TooltipContent>
                Esta filial ainda não está atrelada a um cliente raiz.
              </TooltipContent>
            </Tooltip>
          );
        },
        raw: (filial) => filial.cliente?.nome || "Pendente",
      },
      {
        key: "filial.cnpj",
        title: "CNPJ",
        sortable: true,
        width: "min-w-[150px]",
        render: (filial) => (
          <span className="text-sm text-muted-foreground">{filial.cnpj || "-"}</span>
        ),
        raw: (filial) => filial.cnpj || "",
      },
      {
        key: "filial.ativo",
        title: "Ativo",
        sortable: true,
        width: "min-w-[100px]",
        render: (filial) => <BooleanBadge value={filial.ativo} />,
        raw: (filial) => filial.ativo.toString(),
      },
      {
        key: "criadoPor.nome",
        title: "Criado por",
        sortable: true,
        render: (cliente) =>
          cliente?.criadoPor ? <PersonBadge pessoa={cliente.criadoPor} isCompact={true} /> : "-",
        raw: (cliente) => cliente.criadoPor?.nome || "",
      },
      {
        key: "cliente.criadoEm",
        title: "Criado em",
        sortable: true,
        width: "min-w-[120px]",
        render: (cliente) => (
          <span className="text-sm text-muted-foreground">
            {cliente.criadoEm ? format(new Date(cliente.criadoEm), "dd/MM/yyyy HH:mm") : "-"}
          </span>
        ),
        raw: (cliente) => cliente.criadoEm?.toString() || "",
      },
    ],
    [],
  );

  return (
    <>
      <DataTable
        {...tableProps}
        storageKey={storageKey}
        columns={columns}
        emptyMessage={
          isError
            ? "Não foi possível carregar as filiais. Tente recarregar."
            : "Nenhuma filial cadastrada."
        }
        defaultSortColumn="filial.id"
        defaultSortOrder="DESC"
        searchPlaceholder="Pesquisar filiais por nome ou CNPJ..."
        className="border-none"
      />

      <FilialFormDialog
        open={isFormOpen}
        onOpenChange={(isOpen) => {
          setIsFormOpen(isOpen);
          if (!isOpen) {
            setFilialToEdit(null);
            invalidate();
          }
        }}
        filialToEdit={filialToEdit}
      />

      <AlertDialog open={isStatusAlertOpen} onOpenChange={setIsStatusAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Confirmar {filialToToggleStatus?.ativo ? "Inativação" : "Reativação"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Você tem certeza que deseja {filialToToggleStatus?.ativo ? "inativar" : "reativar"} a
              filial <span className="font-bold text-foreground">{filialToToggleStatus?.nome}</span>
              ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isTogglingStatus}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmStatusToggle();
              }}
              disabled={isTogglingStatus}
              className={
                filialToToggleStatus?.ativo
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : "bg-emerald-600 text-white hover:bg-emerald-700"
              }
            >
              {isTogglingStatus ? "Aguarde..." : "Confirmar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
